import { useCoreService } from 'core/services/useCoreService';
import { createContext, useEffect, useState } from 'react';
import { useDataFetcher } from 'shared/http';
import { useNotifications } from 'shared/notifications';
import { Cuenta } from 'core/cuenta/models/Cuenta';
import { DistribucionDeFondosRequest } from '../models/requests/DistribucionDeFondosRequest';
import { FundsDistributionData } from '../models/FundsDistributionData';
import { useSearchParams } from 'react-router-dom';
import { DISTRIBUTION_ERRORS, GENERIC_MESSAGES } from 'shared/constants/GenericMessages';

interface IFundsDistributionContext {
  origen: Cuenta | null;
  destinos: Cuenta[] | null;
  distributionErrorMessage: string | null;
  isLoading: boolean;
  sendDistribution: (request: DistribucionDeFondosRequest) => void;
  setCuentaOrigen: (account: string | null) => void;
}

export const FundsDistributionContext = createContext<IFundsDistributionContext>({
  origen: null,
  destinos: null,
  distributionErrorMessage: null,
  isLoading: false,
  sendDistribution: () => {},
  setCuentaOrigen: () => {},
});

const mapDistributionErrorMessages = {
  amount_exceeds_max: DISTRIBUTION_ERRORS.amountExceedsMax,
  final_amount_is_zero: DISTRIBUTION_ERRORS.finalAmountIsZero,
};

export const FundsDistributionProvider = ({ children }) => {
  const [fundsDistributionData, setRequest] = useDataFetcher<FundsDistributionData>();
  const [searchParams, setSearchParams] = useSearchParams();
  const coreService = useCoreService();
  const { addNotification } = useNotifications();
  const [cuentaOrigen, setCuentaOrigen] = useState<string | null>(
    searchParams.get('cuentaOrigen'),
  );
  const [distributionErrorMessage, setDistributionErrorMessage] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (cuentaOrigen) {
      setSearchParams({ cuentaOrigen });
    }
    setRequest(() =>
      coreService.getFundsDistributionData(cuentaOrigen).catch((e) => {
        addNotification(
          'Error al obtener los datos para realizar la distribución',
          'error',
        );
        return e;
      }),
    );
  }, [addNotification, setRequest, setSearchParams, coreService, cuentaOrigen]);

  const sendDistribution = (request: DistribucionDeFondosRequest) => {
    setRequest(async () => {
      try {
        await coreService.saveFundsDistribution(request, cuentaOrigen);
        addNotification('Distribución exitosa', 'success');
      } catch (error) {
        setDistributionErrorMessage(
          mapDistributionErrorMessages[error.apiError.message] ||
            GENERIC_MESSAGES.genericError,
        );
      }
      return coreService.getFundsDistributionData(cuentaOrigen);
    });
  };

  const value: IFundsDistributionContext = {
    origen: fundsDistributionData.data?.origen,
    destinos: fundsDistributionData.data?.destinos,
    distributionErrorMessage,
    isLoading: fundsDistributionData.isLoading,
    sendDistribution,
    setCuentaOrigen,
  };

  return (
    <FundsDistributionContext.Provider value={value}>
      {children}
    </FundsDistributionContext.Provider>
  );
};
